import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BilingualColumnHeader } from '../../components/BilingualText/BilingualText';

const formatDate = (dateString) => {
  if (!dateString) return '--';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (e) {
    return 'Invalid Date';
  }
};

const getStatusBadge = (status) => {
  switch ((status || '').toString().toLowerCase()) {
    case 'active': return 'badge badge-success';
    case 'pending': return 'badge badge-warning';
    case 'inactive': return 'badge badge-danger';
    case 'rejected': return 'badge badge-danger';
    default: return 'badge badge-light';
  }
};

const AmountCell = ({ value }) => (
  <span className="text-dark font-w600">₹{parseFloat(value || 0).toLocaleString('en-IN')}</span>
);

const StatusBadge = ({ value }) => (
  <span className={getStatusBadge(value)}>{value || '--'}</span>
);

const DateCell = ({ value }) => (
  value ? <span className="text-muted">{formatDate(value)}</span> : '--'
);

export const useColumns = (currentPage = 1, pageSize = 10) => {
  const { t } = useTranslation();
  
  return useMemo(() => [
  {
    Header: () => <BilingualColumnHeader translationKey="common.serialNo" />,
    accessor: "serialNumber",
    Cell: ({ row }) => {
        const serialNumber = (currentPage - 1) * pageSize + row.index + 1;
        return (
            <div className="align-middle">
                <span className="text-primary fw-bold">
                    {serialNumber}
                </span>
            </div>
        );
    }
  },
  {
    Header: () => <BilingualColumnHeader translationKey="depositors.depositorInfo" />,
    accessor: "name",
    Cell: ({ value, row }) => (
      <div className="d-flex align-items-center">
        <div className="avatar avatar-sm me-2">
          <span className="avatar-title bg-primary text-white rounded-circle">
            {value?.charAt(0)?.toUpperCase()}
          </span>
        </div>
        <div>
          <h6 className="mb-0 text-dark">{value}</h6>
          <small className="text-muted">{row.original.depositorCode}</small>
        </div>
      </div>
    )
  },
  {
    Header: () => <BilingualColumnHeader translationKey="depositors.contactInfo" />,
    accessor: "phoneNo",
    Cell: ({ value, row }) => (
      <div>
        {value && <div className="text-dark">{value}</div>}
        {row.original.email && <small className="d-block text-muted">{row.original.email}</small>}
      </div>
    )
  },
  {
    Header: () => <BilingualColumnHeader translationKey="depositors.location" />,
    accessor: "city",
    Cell: ({ value, row }) => (
      <div className="text-dark">
        {row.original.city && <div>{row.original.city}</div>}
        {row.original.district && <span><small className="text-muted">{row.original.district}</small></span>}
        {row.original.state && <small className="d-block text-muted">{row.original.state}</small>}
        {!row.original.city && !row.original.district && !row.original.state && '--'}
      </div>
    )
  },
  {
    Header: () => <BilingualColumnHeader translationKey="depositors.depositsInfo" />,
    accessor: "totalDeposits",
    Cell: ({ value, row }) => (
      <div>
        <div className="text-dark font-w600">
          {value || 0} Deposits
        </div>
        {row.original.totalAmount && parseFloat(row.original.totalAmount) > 0 && (
          <small className="text-success">
            ₹{parseFloat(row.original.totalAmount).toLocaleString('en-IN')}
          </small>
        )}
      </div>
    )
  },
  {
    Header: () => <BilingualColumnHeader translationKey="common.status" />,
    accessor: "status",
    Cell: ({ value, row }) => (
      <>
      <div>
        <span className={getStatusBadge(value)}>{value || '--'}</span>
      </div>
      <div>
        {row.original.isPartner && (
          <span className="badge badge-info badge-xs mt-2">Partner</span>
        )}
      </div>
      </>
    )
  },
  {
    Header: () => <BilingualColumnHeader translationKey="common.createdDate" />,
    accessor: "createdAt",
    Cell: DateCell
  },
  {
    Header: () => <BilingualColumnHeader translationKey="common.actions" />,
    accessor: "actions",
    Cell: ({ row }) => row.original // This will be handled in the parent component
  }
], [currentPage, pageSize, t]);
};